import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Page, useAccountTimeZone, useOrgName } from '../../layout';
import { fmt } from '../../design/format';
import { useHierarchyFilter } from '../../hooks/useHierarchyFilter';
import { Card } from '../../components/shared/Card';
import { EmptyState, ErrorState, Skeleton } from '../../components/shared/States';
import { Heatmap, useTableToggle } from '../../components/shared/charts';
import { analyticsApi, hourLabel, useAnalytics } from '../../services/analyticsApi';
import { filtersFromQuery } from '../calls/callFilters';
import { RemovableChip } from '../calls/CallFilterChips';

const METRICS = [
  { id: 'calls', api: 'calls', button: 'Calls offered', name: 'Calls offered', format: (v) => fmt.int(v), low: 'Fewer', high: 'More' },
  { id: 'aban', api: 'abandonRate', button: 'Abandon rate', name: 'Abandon rate', format: (v) => `${Number(v).toFixed(1)}%`, low: 'Lower', high: 'Higher' },
  { id: 'wait', api: 'averageWaitSeconds', button: 'Average wait', name: 'Average wait', format: (v) => fmt.mmss(v), low: 'Lower', high: 'Higher' },
  { id: 'sla', api: 'serviceLevelPercent', button: 'SLA missed', name: 'Calls outside SLA', format: (v) => `${Number(v).toFixed(1)}%`, low: 'Lower', high: 'Higher' },
];

const COLUMNS = Array.from({ length: 13 }, (_, i) => i + 7);
const ROW_NAMES = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' };

/** Cell values for the hours shown; "SLA missed" is 100 minus the service level; cells without calls read 0. */
function toGrid(values, metric, hours) {
  return values.map((row) => hours.map((h) => {
    const v = row[h];
    if (v == null) return 0;
    return metric.id === 'sla' ? Math.max(0, 100 - Number(v)) : Number(v);
  }));
}

function staffingNote(metric, peaks, grid, rows, hours) {
  if (!peaks.length) return 'No calls in this period.';
  const [value, row, col] = peaks[0];
  if (metric.id === 'calls') {
    const others = grid.map((r, i) => (i === row ? 0 : r[col]));
    const next = Math.max(...others);
    const above = next > 0 ? Math.round(((value - next) / next) * 100) : null;
    return above != null && above > 0
      ? `${ROW_NAMES[rows[row]]} ${hourLabel(hours[col]).replace('a', ' AM').replace('p', ' PM')} averages ${metric.format(value)} calls, ${above}% above any other weekday at that hour.`
      : `${ROW_NAMES[rows[row]]} ${hourLabel(hours[col]).replace('a', ' AM').replace('p', ' PM')} averages ${metric.format(value)} calls, the busiest hour in this period.`;
  }
  const top = peaks.slice(0, 3).map(([, r, c]) => `${rows[r]} ${hourLabel(hours[c])}`);
  return `Highest values: ${top.join(', ')}.`;
}

export function HeatmapPage() {
  const org = useOrgName();
  const h = useHierarchyFilter();
  const { label: zoneLabel } = useAccountTimeZone();
  const location = useLocation();
  const navigate = useNavigate();
  const linked = filtersFromQuery(location.search);
  const queueIds = linked?.queueIds || [];
  const queueFilter = queueIds.length ? { queueIds: queueIds.join(',') } : {};
  const [metricId, setMetricId] = useState('calls');
  const metric = METRICS.find((m) => m.id === metricId);
  const params = { ...h.params, ...queueFilter, metric: metric.api };
  const { data, error, retry } = useAnalytics(() => analyticsApi.heatmap(params), JSON.stringify(params));
  const table = useTableToggle();

  let body;
  if (error) body = <ErrorState title="Heatmap couldn't load" body="Vertolytics couldn't reach the analytics service." error={error} onRetry={retry} />;
  else if (!data) body = <Skeleton height={420} />;
  else if (!data.totalCalls)
    body = <EmptyState icon="heat" title="No calls in this period" body={`There are no calls in this period to place by weekday and hour. If ${org} just connected, the import may still be running; try Last 30 days once it completes.`} />;
  else {
    const hours = COLUMNS;
    const grid = toGrid(data.values, metric, hours);
    const max = Math.max(...grid.map((r) => Math.max(...r)), 0) || 1;
    const peaks = [];
    grid.forEach((r, i) => r.forEach((v, j) => v > 0 && peaks.push([v, i, j])));
    peaks.sort((a, b) => b[0] - a[0]);
    const nodeGrids = data.byNode.map((n) => ({ ...n, grid: toGrid(n.values, metric, hours) }));
    const nodeMax = Math.max(...nodeGrids.map((n) => Math.max(...n.grid.map((r) => Math.max(...r)))), 0) || 1;
    body = (
      <div className="stack-lg">
        <div className="row-wrap between">
          <div className="seg" role="group" aria-label="Metric">
            {METRICS.map((m) => (
              <button key={m.id} type="button" aria-pressed={m.id === metricId ? 'true' : 'false'} onClick={() => setMetricId(m.id)}>
                {m.button}
              </button>
            ))}
          </div>
          <span className="caption">Darker means more calls, more abandons, longer waits, or more SLA misses.</span>
        </div>
        <div className="grid">
          <Card className="c-9" title={`${metric.name} by weekday and hour`} description={`Average per day, ${zoneLabel}`} actions={table.button}>
            <Heatmap
              rows={data.rows}
              cols={hours.map(hourLabel)}
              values={grid}
              max={max}
              format={metric.format}
              showValues
              cellHeight={38}
              metric={metric.name}
              lowLabel={metric.low}
              highLabel={metric.high}
              aria={`${metric.name} heatmap`}
              showTable={table.on}
            />
          </Card>
          <Card className="c-3" title="Peaks" description="Highest cells for this metric">
            <ol style={{ margin: 0, padding: 0, listStyle: 'none' }} className="stack">
              {peaks.slice(0, 5).map(([v, i, j], index) => (
                <li key={`${i}-${j}`} className="row" style={{ gap: 10 }}>
                  <span className="num caption" style={{ width: 14 }}>
                    {index + 1}
                  </span>
                  <div className="grow">
                    <div className="small strong">
                      {data.rows[i]} {hourLabel(hours[j])}
                    </div>
                    <div className="caption">{metric.format(v)}</div>
                  </div>
                </li>
              ))}
            </ol>
            <div className="panel small mt-4">
              <div className="strong" style={{ marginBottom: 4 }}>
                Staffing note
              </div>
              {staffingNote(metric, peaks, grid, data.rows, hours)}
            </div>
          </Card>
          <Card className="c-12" title={`By ${data.topLevelName}`} description={`Same metric and color scale, so ${data.topLevelName === 'Division' ? 'Divisions' : `${data.topLevelName}s`} compare directly`}>
            <div className="grid">
              {nodeGrids.map((n) => (
                <div key={n.nodeId} className="c-6">
                  <div className="small strong" style={{ marginBottom: 6 }}>
                    {n.name}
                  </div>
                  <Heatmap rows={data.rows} cols={hours.map(hourLabel)} values={n.grid} max={metric.id === 'calls' ? nodeMax : max} format={metric.format} cellHeight={20} labelWidth={36} metric={metric.name} />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <Page
      title="Heatmap"
      subtitle="When calls arrive and when service slips. Each cell is the average for that weekday and hour in the selected period."
      exportConfig={{ name: 'Heatmap', endpoint: '/export/heatmap', params: queueFilter }}
      filters={
        queueIds.length > 0 && (
          <>
            <span className="sep" aria-hidden="true" />
            <RemovableChip id="f-queue" keyLabel="Queue" value={linked.queueName || (queueIds.length === 1 ? '1 queue' : `${queueIds.length} queues`)} clearLabel="Clear queue filter" onClear={() => navigate(location.pathname, { replace: true })} />
          </>
        )
      }
    >
      {body}
    </Page>
  );
}
